import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { ChartData } from 'chart.js';
import { CompanyVendor } from 'src/app/classes/models';
import { PercentageChart } from 'src/app/interfaces/percentage-chart';
import { WidgetPanelChart } from 'src/app/interfaces/widget-panel-chart';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';

type VendorStatusCount = {
  viveStatus: string;
  count: number;
};

@Component({
  selector: 'app-vendor-statuses-chart-widget',
  templateUrl: './vendor-statuses-chart-widget.component.html',
  styleUrls: ['./vendor-statuses-chart-widget.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorStatusesChartWidgetComponent {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private Router: Router,
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService,
    private SharedDataService: SharedDataService
  ) {
    this.SharedDataService.listenForEvent<
      string | null | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'viveAdminStatusLabelSelected' &&
          typeof data === 'string'
        ) {
          this.navigateToRunViveGrid(data);
        }
      }
    });
  }

  @Input() CompanyId!: number;

  title: string = 'Vendor Status';
  percentageCharts: PercentageChart[] = [];
  widgetPanelCharts: WidgetPanelChart[] = [];

  isScreenSizeSmall: boolean =
    (window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth) < 1350;

  private formatData(data: VendorStatusCount[]) {
    const chartData: ChartData = {
      datasets: [
        {
          data: data.map((item) => item.count)
        }
      ],
      labels: data.map((item) => item.viveStatus)
    };

    this.widgetPanelCharts.push({
      title: this.title,
      data: {
        chartType: 'doughnut',
        chartTitle: this.title,
        chartData: chartData,
        chartOptions: {
          maintainAspectRatio: false,
          layout: {
            padding: this.isScreenSizeSmall ? 0 : 15
          },
          aspectRatio: 1,
          cutout: this.isScreenSizeSmall ? '75%' : '80%',
          plugins: {
            legend: {
              display: true,
              position: 'right',
              labels: {
                usePointStyle: true
              }
            },
            tooltip: {
              enabled: true
            }
          },
          events: ['mousemove', 'mouseout', 'touchstart', 'touchmove']
        },
        chartPlugins: [
          {
            id: 'centerText',
            afterDatasetDraw(chart, args, pluginOptions) {
              const { ctx, data } = chart;
              const totalVendorStatuses = String(
                data.datasets[0].data.reduce((acc, current) => {
                  if (!acc) {
                    acc = 0;
                  }
                  if (Array.isArray(current)) {
                    current = current[0];
                  }

                  return Number(acc) + Number(current);
                }, 0)
              );

              const isScreenSizeSmall: boolean =
                (window.innerWidth ||
                  document.documentElement.clientWidth ||
                  document.body.clientWidth) < 1350;

              ctx.save();
              const x = chart.getDatasetMeta(0).data[0].x;
              const y = chart.getDatasetMeta(0).data[0].y;
              ctx.font = isScreenSizeSmall ? '26px Rubik' : '32px Rubik';
              ctx.fillStyle = 'rgb(79, 92, 101)';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(totalVendorStatuses, x, y - 10);

              ctx.font = isScreenSizeSmall ? '14px Rubik' : '18px Rubik';
              ctx.fillText('Vendors', x, y + 15);
            }
          },
          {
            id: 'percentages',
            afterDatasetDraw(chart, args, pluginOptions) {
              const {
                ctx,
                data,
                chartArea: { width, height }
              } = chart;
              const totalVendorStatuses = data.datasets[0].data.reduce(
                (acc, current) => {
                  if (!acc) {
                    acc = 0;
                  }
                  if (Array.isArray(current)) {
                    current = current[0];
                  }

                  return Number(acc) + Number(current);
                },
                0
              );

              let isScreenSizeSmall: boolean =
                (window.innerWidth ||
                  document.documentElement.clientWidth ||
                  document.body.clientWidth) < 1350;
              isScreenSizeSmall = false;

              ctx.save();
              const halfWidth = width / 2;
              const halfHeight = height / 2;

              data.datasets[0].data.forEach((item, index) => {
                let { x, y } = chart
                  .getDatasetMeta(0)
                  .data[index].tooltipPosition(true);

                if (!x || !y) {
                  return;
                }

                const percentage = Math.round(
                  (Number(item) / Number(totalVendorStatuses)) * 100
                ).toString();
                const updatedX = x > halfWidth ? x + 5 : x - 15;
                const updatedY = y >= halfHeight ? y + 25 : y - 20;

                ctx.font = '12px Rubik';
                ctx.fillStyle = isScreenSizeSmall
                  ? 'rgb(255, 255, 255)'
                  : 'rgb(79, 92, 101)';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(
                  `${percentage}%`,
                  isScreenSizeSmall ? x : updatedX,
                  isScreenSizeSmall ? y : updatedY
                );
              });
            }
          }
        ],
        chartStyle: {
          // padding: '1em 0',
        },
        displayChartTitle: false
      }
    });
  }

  private navigateToRunViveGrid(
    viveStatus: CompanyVendor['viveStatus'] | string
  ) {
    this.Router.navigate(['admin', 'run-vive', { viveStatus }]);
  }

  private loadCompanyVendorStatuses() {
    this.CompanyVendorService.getCompanyVendorStatusCount({
      viveStatus: JSON.stringify({
        $in: [
          'Approved',
          'Suspended',
          'Registering',
          'Registered',
          'Reviewing',
          'Awaiting Payment',
          'Renewing',
          'Site Conditional'
        ]
      })
    }).subscribe({
      next: (viveStatusCounts: VendorStatusCount[]) => {
        if (!viveStatusCounts || viveStatusCounts.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No vendor statuses found'
          );
        }

        this.formatData(viveStatusCounts);
        this._cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.loadCompanyVendorStatuses();
  }
}
